/**
 * ChipBuffer.ts - Chip-accurate audio buffer generation
 *
 * Implements NES-accurate synthesis:
 * - LFSR (Linear Feedback Shift Register) noise — replaces Math.random() for authentic drum texture
 * - NES 16-step quantized triangle wave via PeriodicWave
 * - Pre-rendered drum hit buffers with amplitude envelopes baked in
 *
 * All buffer generators are synchronous — no async, no OfflineAudioContext.
 */

import type { DrumTuningSettings } from '../../types/instruments';

// ============================================================
// Core helpers
// ============================================================

/**
 * Quantize samples to 4-bit amplitude resolution (16 levels), NES-accurate.
 * The NES DAC had only 16 volume steps — this "staircase" distortion is part of the sound.
 */
export function apply4BitQuantization(samples: Float32Array): void {
    for (let i = 0; i < samples.length; i++) {
        const s = samples[i] ?? 0;
        const level = Math.round((s + 1.0) * 7.5); // map -1..1 → 0..15
        const clamped = Math.max(0, Math.min(15, level));
        samples[i] = (clamped / 7.5) - 1.0; // map 0..15 → -1..1
    }
}

/**
 * NES LFSR noise step.
 * Long mode (15-bit LFSR, period=32767): authentic snare/noise character
 * Short mode (7-bit LFSR, period=93):   metallic/pitched — authentic hi-hat character
 *
 * @returns next LFSR state (mutate in place via object wrapper)
 */
function lfsrStep(state: { val: number }, mode: 'long' | 'short'): number {
    const bit = mode === 'long'
        ? (state.val ^ (state.val >> 1)) & 1          // feedback: bit0 XOR bit1
        : (state.val ^ (state.val >> 6)) & 1;         // feedback: bit0 XOR bit6
    state.val = ((state.val >> 1) | (bit << 14)) & 0x7FFF;
    return (state.val & 1) ? 0.5 : -0.5;
}

/**
 * Apply a simple one-pole lowpass filter to a sample stream.
 * alpha close to 1 = lower cutoff (more filtering).
 * alpha = exp(-2π * fc / sampleRate)
 */
function onePoleLP(x: number, lpState: { y: number }, alpha: number): number {
    lpState.y = alpha * lpState.y + (1.0 - alpha) * x;
    return lpState.y;
}

/**
 * Apply a simple one-pole highpass filter.
 * Implemented as: hp = input - lowpass(input)
 */
function onePoleHP(x: number, hpState: { lp: number }, alpha: number): number {
    hpState.lp = alpha * hpState.lp + (1.0 - alpha) * x;
    return x - hpState.lp;
}

/**
 * Biquad bandpass filter state + coefficients computed once.
 * Uses RBJ Audio EQ cookbook constant-peak-gain bandpass formula.
 */
interface BiquadState {
    x1: number; x2: number; y1: number; y2: number;
    b0a0: number; b2a0: number; a1a0: number; a2a0: number;
}

function makeBandpass(fc: number, Q: number, sampleRate: number): BiquadState {
    const w0 = 2 * Math.PI * fc / sampleRate;
    const sinW0 = Math.sin(w0);
    const cosW0 = Math.cos(w0);
    const alpha = sinW0 / (2 * Q);
    const a0 = 1 + alpha;
    return {
        x1: 0, x2: 0, y1: 0, y2: 0,
        b0a0: alpha / a0,
        b2a0: -alpha / a0,
        a1a0: (-2 * cosW0) / a0,
        a2a0: (1 - alpha) / a0,
    };
}

function applyBandpass(x: number, s: BiquadState): number {
    const y = s.b0a0 * x + s.b2a0 * s.x2 - s.a1a0 * s.y1 - s.a2a0 * s.y2;
    s.x2 = s.x1; s.x1 = x;
    s.y2 = s.y1; s.y1 = y;
    return y;
}

// ============================================================
// NES Triangle PeriodicWave
// ============================================================

/**
 * Create a NES-accurate 16-step quantized triangle PeriodicWave.
 *
 * The NES triangle channel had 4-bit amplitude resolution (16 steps).
 * At higher pitches the staircase is audible as a buzzy harmonic distortion —
 * this is the signature NES bass/lead sound.
 *
 * The 32-step waveform (one period): 15,14,...,1,0,0,1,...,14,15
 * Normalized to -1..1 and transformed via DFT → Fourier series → PeriodicWave.
 */
export function createNESTriangleWave(audioContext: AudioContext): PeriodicWave {
    // 32-step NES triangle sequence (4-bit, range 0-15)
    const nesSteps = [
        15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0,
        0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15,
    ];
    const N = 32;
    const harmonics = 64; // enough for all audible harmonics

    const real = new Float32Array(harmonics + 1);
    const imag = new Float32Array(harmonics + 1);
    real[0] = 0; // no DC offset
    imag[0] = 0;

    // Compute Fourier coefficients via DFT
    // real[k] = (2/N) * sum( x[n] * cos(2π*k*n/N) )
    // imag[k] = (2/N) * sum( x[n] * sin(2π*k*n/N) )
    for (let k = 1; k <= harmonics; k++) {
        let re = 0;
        let im = 0;
        for (let n = 0; n < N; n++) {
            const value = (nesSteps[n]! / 15.0) * 2.0 - 1.0; // normalize to -1..1
            const angle = (2 * Math.PI * k * n) / N;
            re += value * Math.cos(angle);
            im += value * Math.sin(angle);
        }
        real[k] = (2 / N) * re;
        imag[k] = (2 / N) * im;
    }

    return audioContext.createPeriodicWave(real, imag, { disableNormalization: false });
}

/**
 * Aliased square wave via PeriodicWave.
 * Uses the standard Fourier series for a square wave (4/(nπ) for odd harmonics)
 * WITHOUT band-limiting, so harmonics beyond Nyquist produce authentic aliasing artifacts.
 */
export function createAliasedSquareWave(
    audioContext: AudioContext,
    harmonicCount: number = 256,
): PeriodicWave {
    const count = Math.max(1, Math.min(4096, Math.round(harmonicCount)));
    const real = new Float32Array(count + 1);
    const imag = new Float32Array(count + 1);

    for (let n = 1; n <= count; n++) {
        if (n % 2 === 1) {
            imag[n] = 4 / (n * Math.PI);
        }
        // even harmonics and all real coefficients stay 0
    }

    return audioContext.createPeriodicWave(real, imag, { disableNormalization: true });
}

/**
 * Aliased sawtooth wave via PeriodicWave.
 * Uses the standard Fourier series (2/π × (-1)^(n+1) / n for all harmonics)
 * WITHOUT band-limiting, producing authentic aliasing artifacts.
 */
export function createAliasedSawtoothWave(
    audioContext: AudioContext,
    harmonicCount: number = 256,
): PeriodicWave {
    const count = Math.max(1, Math.min(4096, Math.round(harmonicCount)));
    const real = new Float32Array(count + 1);
    const imag = new Float32Array(count + 1);

    for (let n = 1; n <= count; n++) {
        imag[n] = (2 / Math.PI) * (Math.pow(-1, n + 1) / n);
    }

    return audioContext.createPeriodicWave(real, imag, { disableNormalization: true });
}

/**
 * Stepped sawtooth wave via PeriodicWave.
 * Approximates a sawtooth as a multi-step staircase (e.g. 7 steps),
 * matching how real chip hardware generated sawtooth with limited DAC resolution.
 * Uses the same DFT approach as createNESTriangleWave.
 */
export function createSteppedSawtoothWave(
    audioContext: AudioContext,
    steps: number = 7,
): PeriodicWave {
    const N = Math.max(2, Math.min(64, Math.round(steps)));
    const harmonics = 64;

    // Build one period of the staircase: N equal-width segments
    // at evenly spaced amplitudes from -1 to +1 (rising sawtooth)
    const samples = new Float32Array(N);
    for (let i = 0; i < N; i++) {
        samples[i] = (2 * i) / (N - 1) - 1; // maps 0→-1, N-1→+1
    }

    const real = new Float32Array(harmonics + 1);
    const imag = new Float32Array(harmonics + 1);

    // DFT to derive Fourier coefficients (same approach as createNESTriangleWave)
    for (let k = 1; k <= harmonics; k++) {
        let re = 0;
        let im = 0;
        for (let n = 0; n < N; n++) {
            const angle = (2 * Math.PI * k * n) / N;
            re += samples[n]! * Math.cos(angle);
            im += samples[n]! * Math.sin(angle);
        }
        real[k] = (2 / N) * re;
        imag[k] = (2 / N) * im;
    }

    return audioContext.createPeriodicWave(real, imag, { disableNormalization: false });
}

// ============================================================
// LFSR-based noise generators (Req 8)
// ============================================================

/**
 * Generate white noise using a 15-bit LFSR (long mode, period 32767).
 * Deterministic — same inputs always produce the same output.
 */
export function generateLFSRWhiteNoise(sampleRate: number, duration: number): Float32Array {
    if (sampleRate <= 0 || duration <= 0) return new Float32Array(0);

    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);
    const lfsr = { val: 0x7FFF };

    for (let i = 0; i < n; i++) {
        out[i] = lfsrStep(lfsr, 'long');
    }
    return out;
}

/**
 * Generate pink (1/f) noise using the Voss-McCartney algorithm with LFSR as the random source.
 * Uses 16 octave rows, each updating at halving rates, summed and normalized.
 * Deterministic — same inputs always produce the same output.
 */
export function generateLFSRPinkNoise(sampleRate: number, duration: number): Float32Array {
    if (sampleRate <= 0 || duration <= 0) return new Float32Array(0);

    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);
    const NUM_ROWS = 16;

    // Each row has its own LFSR with a unique seed
    const rows: { lfsr: { val: number }; value: number }[] = [];
    for (let r = 0; r < NUM_ROWS; r++) {
        rows.push({ lfsr: { val: (0x7FFF - r * 1999) & 0x7FFF || 1 }, value: 0 });
    }
    // White noise source for the "running sum" contribution
    const whiteLfsr = { val: 0x7FFF };

    // Initialize row values
    for (const row of rows) {
        row.value = lfsrStep(row.lfsr, 'long');
    }

    for (let i = 0; i < n; i++) {
        // Determine which row to update using trailing zeros of the counter
        // Row r updates every 2^r samples (Voss-McCartney scheduling)
        let sum = 0;
        for (let r = 0; r < NUM_ROWS; r++) {
            if (i > 0 && (i & ((1 << r) - 1)) === 0) {
                rows[r]!.value = lfsrStep(rows[r]!.lfsr, 'long');
            }
            sum += rows[r]!.value;
        }
        // Add white noise contribution for inter-sample variation
        sum += lfsrStep(whiteLfsr, 'long');

        // Normalize: each source contributes ±0.5, total range is ±(NUM_ROWS+1)*0.5
        out[i] = sum / ((NUM_ROWS + 1) * 0.5);
    }
    return out;
}

/**
 * Generate brown (1/f²) noise using a random-walk algorithm with LFSR as the step source.
 * Each sample is the previous sample plus a small LFSR-derived step, clamped to [-1, 1].
 * Deterministic — same inputs always produce the same output.
 */
export function generateLFSRBrownNoise(sampleRate: number, duration: number): Float32Array {
    if (sampleRate <= 0 || duration <= 0) return new Float32Array(0);

    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);
    const lfsr = { val: 0x7FFF };

    let value = 0;
    const stepScale = 0.04; // small step size to keep walk bounded

    for (let i = 0; i < n; i++) {
        const step = lfsrStep(lfsr, 'long') * stepScale; // ±0.5 * stepScale = ±0.02
        value += step;
        // Clamp to [-1, 1]
        if (value > 1) value = 1;
        else if (value < -1) value = -1;
        out[i] = value;
    }
    return out;
}

// ============================================================
// Drum tuning helpers
// ============================================================

/** Clamp a value to [min, max], returning defaultVal when value is undefined. */
function clampOr(value: number | undefined, min: number, max: number, defaultVal: number): number {
    if (value === undefined) return defaultVal;
    return Math.max(min, Math.min(max, value));
}

// ============================================================
// Drum buffer generators
// ============================================================

/**
 * NES-style kick drum.
 * Sine wave with fast pitch sweep (150→40 Hz) + LFSR click transient (lowpassed).
 * Total duration: 400ms.
 */
export function generateKickBuffer(sampleRate: number, tuning?: DrumTuningSettings): Float32Array {
    const pitchStart = clampOr(tuning?.pitchStart, 40, 500, 150);
    const pitchEnd = clampOr(tuning?.pitchEnd, 20, 300, 40);
    const decayRate = clampOr(tuning?.decayRate, 0.01, 1.0, 0.12);

    const duration = 0.4;
    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);

    const lfsr = { val: 0x7FFF };
    const lpState = { y: 0 };
    const lpAlpha = Math.exp(-2 * Math.PI * 200 / sampleRate); // 200 Hz lowpass for click

    let phase = 0;

    for (let i = 0; i < n; i++) {
        const t = i / sampleRate;

        // Pitch envelope: exponential sweep from pitchStart → pitchEnd
        const freq = pitchEnd + (pitchStart - pitchEnd) * Math.exp(-t / 0.025);
        phase += (2 * Math.PI * freq) / sampleRate;
        if (phase >= 2 * Math.PI) phase -= 2 * Math.PI;
        const body = Math.sin(phase);

        // Short-mode LFSR noise burst → lowpass 200 Hz → click transient (first 30ms)
        let click = 0;
        if (t < 0.03) {
            const raw = lfsrStep(lfsr, 'short');
            const filtered = onePoleLP(raw, lpState, lpAlpha);
            click = filtered * Math.exp(-t / 0.008) * 0.5;
        }

        // Amplitude envelope
        const amp = Math.exp(-t / decayRate);
        out[i] = (body * 0.9 + click) * amp;
    }

    apply4BitQuantization(out);
    return out;
}

/**
 * NES-style snare drum.
 * Long-mode LFSR noise through bandpass (2500 Hz) + quantized triangle body (180→80 Hz sweep).
 * Total duration: 200ms.
 */
export function generateSnareBuffer(sampleRate: number, tuning?: DrumTuningSettings): Float32Array {
    const pitchStart = clampOr(tuning?.pitchStart, 40, 500, 180);
    const pitchEnd = clampOr(tuning?.pitchEnd, 20, 300, 80);
    const decayRate = clampOr(tuning?.decayRate, 0.01, 1.0, 0.07);
    const noiseTone = clampOr(tuning?.noiseTone, 200, 12000, 2500);

    const duration = 0.2;
    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);

    const lfsr = { val: 0x7FFF };
    const bp = makeBandpass(noiseTone, 3.0, sampleRate);

    let phase = 0;

    for (let i = 0; i < n; i++) {
        const t = i / sampleRate;

        // Long-mode LFSR noise → bandpass for snare wire character
        const noiseRaw = lfsrStep(lfsr, 'long');
        const noise = applyBandpass(noiseRaw, bp);

        // Quantized triangle body with pitch sweep
        const bodyFreq = pitchEnd + (pitchStart - pitchEnd) * Math.exp(-t / 0.03);
        phase += (2 * Math.PI * bodyFreq) / sampleRate;
        if (phase >= 2 * Math.PI) phase -= 2 * Math.PI;
        const triPhase = phase / (2 * Math.PI);
        const rawTri = triPhase < 0.5 ? triPhase * 2 : (1 - triPhase) * 2;
        const step = Math.floor(rawTri * 15.99);
        const body = (step / 15) * 2 - 1; // NES 16-step quantized

        const amp = Math.exp(-t / decayRate);
        out[i] = (noise * 0.65 + body * 0.35) * amp;
    }

    apply4BitQuantization(out);
    return out;
}

/**
 * NES-style hi-hat.
 * Short-mode LFSR (period=93, metallic texture) → one-pole highpass (7 kHz).
 * Closed: 80ms. Open: 280ms.
 */
export function generateHiHatBuffer(sampleRate: number, isOpen: boolean, tuning?: DrumTuningSettings): Float32Array {
    const noiseTone = clampOr(tuning?.noiseTone, 200, 12000, 7000);
    const decayRate = tuning?.decayRate !== undefined
        ? clampOr(tuning.decayRate, 0.01, 1.0, isOpen ? 0.11 : 0.022)
        : (isOpen ? 0.11 : 0.022);

    const duration = isOpen ? 0.28 : 0.08;
    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);

    const lfsr = { val: 0x7FFF };
    const hpAlpha = Math.exp(-2 * Math.PI * noiseTone / sampleRate);
    const hpState = { lp: 0 };

    for (let i = 0; i < n; i++) {
        const t = i / sampleRate;
        const raw = lfsrStep(lfsr, 'short');
        const hp = onePoleHP(raw, hpState, hpAlpha);
        const amp = Math.exp(-t / decayRate);
        out[i] = hp * amp;
    }

    apply4BitQuantization(out);
    return out;
}

/**
 * Tom drum — NES 16-step quantized triangle body with pitch envelope and exponential decay.
 * Duration: 400ms.
 */
export function generateTomBuffer(sampleRate: number, pitch: 'low' | 'mid' | 'high', tuning?: DrumTuningSettings): Float32Array {
    const duration = 0.4;
    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);

    let defaultStart: number;
    let defaultEnd: number;
    switch (pitch) {
        case 'low': defaultStart = 120; defaultEnd = 55; break;
        case 'high': defaultStart = 260; defaultEnd = 140; break;
        default: defaultStart = 180; defaultEnd = 95; break;
    }

    const startFreq = clampOr(tuning?.pitchStart, 40, 500, defaultStart);
    const endFreq = clampOr(tuning?.pitchEnd, 20, 300, defaultEnd);
    const decayRate = clampOr(tuning?.decayRate, 0.01, 1.0, 0.14);

    let phase = 0;

    for (let i = 0; i < n; i++) {
        const t = i / sampleRate;
        // Exponential pitch envelope sweep from startFreq to endFreq
        const freq = endFreq + (startFreq - endFreq) * Math.exp(-t / 0.04);
        phase += (2 * Math.PI * freq) / sampleRate;
        if (phase >= 2 * Math.PI) phase -= 2 * Math.PI;

        // NES 16-step quantized triangle: map phase to 0–15 step, convert to -1..1
        const triPhase = phase / (2 * Math.PI);
        const rawTri = triPhase < 0.5 ? triPhase * 2 : (1 - triPhase) * 2;
        const step = Math.floor(rawTri * 15.99);
        const body = (step / 15) * 2 - 1;

        const amp = Math.exp(-t / decayRate);
        out[i] = body * amp;
    }

    apply4BitQuantization(out);
    return out;
}

/**
 * Cymbal — short-mode LFSR (metallic) mixed with a second LFSR offset for shimmer,
 * highpassed to remove low-frequency mud.
 * Crash: 800ms. Ride: 250ms.
 */
export function generateCymbalBuffer(sampleRate: number, type: 'crash' | 'ride', tuning?: DrumTuningSettings): Float32Array {
    const noiseTone = clampOr(tuning?.noiseTone, 200, 12000, 4000);
    const decayRate = tuning?.decayRate !== undefined
        ? clampOr(tuning.decayRate, 0.01, 1.0, type === 'crash' ? 0.22 : 0.09)
        : (type === 'crash' ? 0.22 : 0.09);

    const duration = type === 'crash' ? 0.8 : 0.25;
    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);

    const lfsr1 = { val: 0x7FFF };
    const lfsr2 = { val: 0x5A3C }; // Different start for shimmer layer

    // Highpass state for main layer and shimmer layer (shimmer at 2× noiseTone)
    const hp1State = { lp: 0 };
    const hp1Alpha = Math.exp(-2 * Math.PI * noiseTone / sampleRate);
    const hp2State = { lp: 0 };
    const hp2Alpha = Math.exp(-2 * Math.PI * Math.min(noiseTone * 2, 12000) / sampleRate);

    for (let i = 0; i < n; i++) {
        const t = i / sampleRate;
        const raw1 = lfsrStep(lfsr1, 'short');
        const raw2 = lfsrStep(lfsr2, 'short');
        const hp1 = onePoleHP(raw1, hp1State, hp1Alpha);
        const hp2 = onePoleHP(raw2, hp2State, hp2Alpha);
        const amp = Math.exp(-t / decayRate);
        out[i] = (hp1 * 0.7 + hp2 * 0.3) * amp;
    }

    apply4BitQuantization(out);
    return out;
}

/**
 * Clap — four overlapping bursts of long-mode LFSR noise (0/10/20/30ms offsets),
 * each shaped with a short bandpass envelope. Simulates multiple hands clapping.
 * Total duration: 120ms.
 */
export function generateClapBuffer(sampleRate: number, tuning?: DrumTuningSettings): Float32Array {
    const noiseTone = clampOr(tuning?.noiseTone, 200, 12000, 1500);
    const decayRate = clampOr(tuning?.decayRate, 0.01, 1.0, 0.012);

    const duration = 0.12;
    const n = Math.ceil(sampleRate * duration);
    const out = new Float32Array(n);

    const burstOffsets = [0, 0.01, 0.02, 0.03]; // seconds

    for (const offset of burstOffsets) {
        const lfsr = { val: 0x7FFF };
        const bp = makeBandpass(noiseTone, 2.0, sampleRate);
        const startSample = Math.floor(offset * sampleRate);

        for (let i = startSample; i < n; i++) {
            const t = (i - startSample) / sampleRate;
            const raw = lfsrStep(lfsr, 'long');
            const filtered = applyBandpass(raw, bp);
            const amp = Math.exp(-t / decayRate);
            out[i] = (out[i] ?? 0) + filtered * amp * 0.5;
        }
    }

    apply4BitQuantization(out);
    return out;
}
